import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import IMG_APP_ICON from 'src/assets/app-icon-small.jpg'
import { config } from 'src/utils/config'
import { track } from 'src/utils/tracker'
import { isMobileDevice } from 'src/utils/ua'
import { isAndroid, isIOS, isSafari } from 'src/utils/userAgent'
import { openAppDownloadDialog } from './AppDownload'

function openInIOSURLScheme(pathname: string) {
  // TODO: to be updated
  const url = new URL(`art.pixai.pixai://${pathname}`)
  return url.toString()
}

const BANNER_HEIGHT = 50

function OpenAppBannerContent() {
  const { t } = useTranslation('common')

  const [isAndroidAppInstalled, setIsAndroidAppInstalled] = useState(false)

  const shouldNotRedirect = useRef(false)
  const handleOnFocus = () => {
    shouldNotRedirect.current = false
  }
  const handleOnBlur = () => {
    shouldNotRedirect.current = true
  }

  /**
   *
   * This function is not usable until url scheme support is added to iOS App
   * @see https://stackoverflow.com/questions/56528191/how-to-fix-deep-linking-in-ios-when-the-application-has-not-installed-in-the-dev
   */
  const delayedAppStoreRedirection = (
    e: React.MouseEvent<HTMLAnchorElement>,
    pathname: string,
  ) => {
    // iOS Safari only
    if (isIOS()) {
      e.preventDefault()
    } else {
      return
    }

    const schemeUrl = openInIOSURLScheme(pathname)
    setTimeout(() => {
      if (!shouldNotRedirect.current) {
        if (window.top) {
          window.top.location.href = config.iosAppStoreLink
        }
      }
      // has to be at least 3000, or will open both appstore and app
    }, 3000)
    if (window.top) {
      window.top.location.href = schemeUrl
    }
  }

  /**
   * @see https://web.dev/get-installed-related-apps
   */
  const checkAndroidAppInstalled = async () => {
    if (
      'getInstalledRelatedApps' in navigator &&
      typeof navigator.getInstalledRelatedApps === 'function'
    ) {
      try {
        const relatedApps = await navigator.getInstalledRelatedApps()
        if (Array.isArray(relatedApps) && relatedApps.length > 0) {
          setIsAndroidAppInstalled(true)
        }
      } catch {
        // noop
      }
    }
  }

  useEffect(() => {
    if (isAndroid()) {
      checkAndroidAppInstalled()
    }
  }, [])

  useEffect(() => {
    window.addEventListener('focus', handleOnFocus)
    window.addEventListener('blur', handleOnBlur)
    return () => {
      window.removeEventListener('focus', handleOnFocus)
      window.removeEventListener('blur', handleOnBlur)
    }
  }, [])

  const buttonHref = (() => {
    if (isAndroid()) {
      if (!isAndroidAppInstalled) {
        return config.playStoreLink
      }
      // Android url scheme support is removed from the app
      // return `art.pixai.pixai://app`
    }
    // just make all iOS users go to app store
    if (isIOS()) {
      return config.iosAppStoreLink
    }

    return window.location.href
  })()

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--open-in-app-banner-height',
      BANNER_HEIGHT + 'px',
    )
    return () => {
      document.documentElement.style.removeProperty(
        '--open-in-app-banner-height',
      )
    }
  }, [])

  return (
    <section
      className="box-content px-3 flex items-center gap-2 text-xs bg-background border-b border-neutral-200 dark:border-neutral-700"
      style={{ height: BANNER_HEIGHT }}
    >
      <img src={IMG_APP_ICON} alt="PixAI app icon" className="w-7 h-7 rounded" />
      <div className="flex flex-col leading-none overflow-hidden">
        <strong className="text-sm">PixAI</strong>
        <span className="w-full truncate opacity-60">
          {t('common:use-app-for-the-better-xp')}
        </span>
      </div>
      <a
        href={buttonHref}
        target="_blank"
        rel="noreferrer"
        className="px-3 py-1.5 text-[15px] font-medium text-white bg-[#017AFE] rounded-full ml-auto"
        onClick={e => {
          track('download_click')
          if (isAndroid()) {
            e.preventDefault()
            openAppDownloadDialog({
              hideIos: true,
              trackingArgs: { source: 'user' },
            })
          }
        }}
      >
        {t('common:open-in-app')}
      </a>
    </section>
  )
}

export default function OpenAppBanner() {
  // We use the Smart App Banner for iOS Safari users.
  // So no need to display ours.
  return isMobileDevice() && !isSafari() ? <OpenAppBannerContent /> : null
}
